/**
 * Identity and categorisation for one-click sync-run issue reports.
 *
 * Kept apart from the button so the rules can be unit-tested without pulling in
 * MUI or the API client.
 */

export interface SyncIssueReportTarget {
  productId: string;
  productSku?: string;
  productName?: string;
  productBrand?: string;
  /** Pipeline message shown in the run panel — submitted as the description. */
  message: string;
  /**
   * Pipeline code, e.g. below_map, no_valid_price. Stable across runs, so it
   * identifies the problem on its own. Absent on rows the pipeline left
   * uncoded — those fall back to the message for identity.
   */
  code?: string;
  /** Label for rows with no pipeline code. Recorded and shown, never keyed on. */
  fallbackCode?: string;
  syncRunId?: string;
  integrationId?: string;
  integrationName?: string;
}

/** FNV-1a. Only needs to be stable and well-spread — not cryptographic. */
const hashMessage = (message: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < message.length; i += 1) {
    hash ^= message.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

/**
 * Same problem on the same product = same key, whatever run surfaced it.
 *
 * A pipeline code is the identity when there is one. Uncoded rows key on their
 * message instead: falling back to a shared constant would make two unrelated
 * warnings — or a warning and a failure — on one product collapse into each
 * other, so reporting one would silently mark the other as reported.
 */
export const syncIssueSourceKey = (issue: {
  productId: string;
  message: string;
  code?: string;
}): string => `sync:${issue.productId}:${issue.code || `msg-${hashMessage(issue.message)}`}`;

/** Pipeline code → the issue category admins triage on. */
export const syncIssueCategory = (code?: string): string => {
  switch (code) {
    case 'below_map':
    case 'suspicious_discount':
      return 'incorrect_price';
    case 'no_valid_price':
      // Nothing to push because the catalog has no advertised price at all.
      return 'missing_data';
    default:
      return 'other';
  }
};
